import React, { useEffect, useState } from "react";
import { BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";

const b64ToUint8 = (s) => {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const raw = atob((s + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

const getReg = async () => (await navigator.serviceWorker.getRegistration()) || navigator.serviceWorker.register("/sw.js");

async function subscribeDevice() {
  const { data } = await api.get("/push/vapid-public-key");
  const reg = await getReg();
  await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(data.public_key) });
  await api.post("/push/subscribe", { subscription: sub.toJSON() });
}

async function currentSubscription() {
  const reg = await navigator.serviceWorker.getRegistration();
  return reg ? reg.pushManager.getSubscription() : null;
}

function usePushState() {
  const [state, setState] = useState("loading");
  useEffect(() => {
    if (!supported()) return setState("unsupported");
    if (Notification.permission === "denied") return setState("denied");
    currentSubscription().then((sub) => setState(sub ? "on" : "off")).catch(() => setState("off"));
  }, []);
  return [state, setState];
}

async function enablePush(setState) {
  try {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") { setState("denied"); return toast.error("Notifications were blocked in your browser settings"); }
    await subscribeDevice();
    setState("on");
    toast.success("Push alerts enabled on this device");
    api.post("/push/test").catch(() => {});
  } catch (e) { toast.error("Could not enable push alerts"); }
}

async function disablePush(setState) {
  const sub = await currentSubscription();
  if (sub) { await api.delete("/push/unsubscribe", { data: { subscription: sub.toJSON() } }).catch(() => {}); await sub.unsubscribe(); }
  setState("off");
  toast.success("Push alerts turned off on this device");
}

export const PushToggle = ({ compact = false }) => {
  const [state, setState] = usePushState();
  const enable = () => enablePush(setState);
  const disable = () => disablePush(setState);

  if (state === "loading") return null;
  if (state === "unsupported") return <p className="text-xs text-muted-foreground" data-testid="push-unsupported">Push alerts need the app installed to your home screen (iPhone) or a modern browser.</p>;
  if (state === "denied") return <p className="text-xs text-muted-foreground" data-testid="push-denied">Notifications are blocked. Allow them in your browser/site settings to receive alerts.</p>;
  const on = state === "on";
  return (
    <Button onClick={on ? disable : enable} variant={on ? "outline" : "default"} size={compact ? "sm" : "default"}
      className={on ? "" : "bg-crimson"} data-testid="push-toggle-btn">
      {on ? <BellOff className="h-4 w-4 mr-2" /> : <BellRing className="h-4 w-4 mr-2" />}
      {on ? "Turn off push alerts" : "Enable push alerts"}
    </Button>
  );
};
